/**
 * Organization Switcher Component
 *
 * Standalone dropdown for switching between organizations.
 * Useful when you need organization switching separate from the user menu.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { Organization, User } from '../types';
import { portalAPI } from '../services/api';
import { eventBus, Events } from '../services/events';
import { brandingService } from '../services/branding';

@customElement('moya-organization-switcher')
export class MoyaOrganizationSwitcher extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
      font-family: var(--moya-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
    }

    .trigger {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 12px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      cursor: pointer;
      color: white;
      transition: all 0.15s ease;
      min-width: 180px;
    }

    .trigger:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .trigger:focus {
      outline: none;
      box-shadow: 0 0 0 2px var(--moya-primary-color, rgba(59, 130, 246, 0.5));
    }

    .trigger.open {
      background: rgba(255, 255, 255, 0.15);
    }

    .org-logo {
      width: 24px;
      height: 24px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      background: var(--moya-primary-color, #3b82f6);
      color: white;
      flex-shrink: 0;
      overflow: hidden;
    }

    .org-logo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .org-info {
      flex: 1;
      min-width: 0;
      text-align: left;
    }

    .org-label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.5);
      line-height: 1;
      margin-bottom: 2px;
    }

    .org-name {
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .chevron {
      width: 16px;
      height: 16px;
      color: rgba(255, 255, 255, 0.5);
      transition: transform 0.15s ease;
      flex-shrink: 0;
    }

    .trigger.open .chevron {
      transform: rotate(180deg);
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      right: 0;
      min-width: 240px;
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      z-index: 1000;
      display: none;
    }

    .dropdown.open {
      display: block;
    }

    .dropdown-header {
      padding: 12px 16px 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.4);
    }

    .org-list {
      padding: 4px 8px 8px;
      max-height: 300px;
      overflow-y: auto;
    }

    .org-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .org-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .org-item.active {
      background: rgba(var(--moya-primary-color, 59, 130, 246), 0.1);
    }

    .org-item .org-logo {
      width: 32px;
      height: 32px;
      font-size: 13px;
    }

    .org-item .org-details {
      flex: 1;
      min-width: 0;
    }

    .org-item .org-name {
      font-size: 14px;
      color: white;
      margin-bottom: 2px;
    }

    .org-item .org-slug {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
    }

    .org-check {
      color: var(--moya-primary-color, #3b82f6);
      width: 18px;
      height: 18px;
    }

    .no-orgs {
      padding: 16px;
      text-align: center;
      color: rgba(255, 255, 255, 0.5);
      font-size: 13px;
    }

    /* Loading state */
    .loading {
      padding: 16px;
      text-align: center;
    }

    .loading-spinner {
      width: 24px;
      height: 24px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top-color: var(--moya-primary-color, #3b82f6);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin: 0 auto;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: Object }) user: User | null = null;
  @property({ type: Array }) organizations: Organization[] = [];
  @property({ type: String }) activeOrganizationId = '';
  @property({ type: Boolean }) compact = false;

  @state() private isOpen = false;
  @state() private loading = false;

  private clickOutsideHandler: ((e: MouseEvent) => void) | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);

    // Use user's organizations if available
    if (this.user?.organizations) {
      this.organizations = this.user.organizations;
      this.activeOrganizationId = this.user.activeOrganizationId || '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('user') && this.user) {
      this.organizations = this.user.organizations || [];
      this.activeOrganizationId = this.user.activeOrganizationId || '';
    }
  }

  private handleClickOutside(e: MouseEvent) {
    if (this.isOpen && !this.contains(e.target as Node)) {
      this.isOpen = false;
    }
  }

  private toggleDropdown(e: Event) {
    e.stopPropagation();
    if (this.organizations.length > 1) {
      this.isOpen = !this.isOpen;
    }
  }

  private get activeOrganization(): Organization | undefined {
    return this.organizations.find((o) => o.id === this.activeOrganizationId);
  }

  private async handleOrgSwitch(org: Organization) {
    if (org.id === this.activeOrganizationId || this.loading) {
      return;
    }

    this.loading = true;

    try {
      await portalAPI.switchOrganization(org.id);

      this.activeOrganizationId = org.id;
      this.isOpen = false;

      // Apply organization branding
      brandingService.applyOrganizationBranding(org);

      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent('moya-organization-switch', {
          bubbles: true,
          composed: true,
          detail: { organization: org },
        })
      );

      eventBus.emit(Events.ORGANIZATION_SWITCHED, org);

      // Reload page to reflect organization change
      window.location.reload();
    } catch (e) {
      console.error('[OrganizationSwitcher] Failed to switch organization:', e);
    } finally {
      this.loading = false;
    }
  }

  render() {
    const activeOrg = this.activeOrganization;

    // Don't render if only one or no organizations
    if (this.organizations.length <= 1) {
      return html``;
    }

    const triggerClasses = classMap({
      trigger: true,
      open: this.isOpen,
    });

    const dropdownClasses = classMap({
      dropdown: true,
      open: this.isOpen,
    });

    return html`
      <button
        class="${triggerClasses}"
        @click="${this.toggleDropdown}"
        aria-label="Switch organization"
        aria-expanded="${this.isOpen}"
        aria-haspopup="listbox"
      >
        <div class="org-logo" style="${activeOrg?.primaryColor ? `background: ${activeOrg.primaryColor}` : ''}">
          ${activeOrg?.logoUrl
            ? html`<img src="${activeOrg.logoUrl}" alt="" />`
            : (activeOrg?.name?.charAt(0) || 'O')}
        </div>
        <div class="org-info">
          <div class="org-label">Organization</div>
          <div class="org-name">${activeOrg?.name || 'Select Organization'}</div>
        </div>
        <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="${dropdownClasses}" role="listbox">
        <div class="dropdown-header">Switch Organization</div>

        ${this.loading
          ? html`
              <div class="loading">
                <div class="loading-spinner"></div>
              </div>
            `
          : html`
              <div class="org-list">
                ${this.organizations.map(
                  (org) => html`
                    <div
                      class="org-item ${org.id === this.activeOrganizationId ? 'active' : ''}"
                      role="option"
                      aria-selected="${org.id === this.activeOrganizationId}"
                      @click="${() => this.handleOrgSwitch(org)}"
                    >
                      <div class="org-logo" style="${org.primaryColor ? `background: ${org.primaryColor}` : ''}">
                        ${org.logoUrl
                          ? html`<img src="${org.logoUrl}" alt="" />`
                          : org.name.charAt(0)}
                      </div>
                      <div class="org-details">
                        <div class="org-name">${org.name}</div>
                        <div class="org-slug">${org.slug}</div>
                      </div>
                      ${org.id === this.activeOrganizationId
                        ? html`
                            <svg class="org-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          `
                        : ''}
                    </div>
                  `
                )}
              </div>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-organization-switcher': MoyaOrganizationSwitcher;
  }
}
